import {AfterViewInit, Component, OnDestroy, ViewChild, ElementRef} from '@angular/core';
import {AuthService} from '../../../@core/services/auth.service';
import {CompanyResponse, UserEntityRelationWithoutUserDataResponse, UserResponse} from '../../models/response.module';
import {UserService} from '../../../@core/services/user.service';
import {CompanyService} from '../../services/company.service';
import {COR_COY_4} from '../../models/input.module';
import {StorageService} from '../../../@core/services/storage.service';
import {forkJoin, of, Subscription} from 'rxjs';
import {NavigationEnd, Router} from '@angular/router';
import {catchError, filter, map} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';

declare const KTMenu: any;
declare const bootstrap: any;

@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.html',
  styleUrl: './header.css'
})
export class Header implements OnDestroy, AfterViewInit {
  isLoggedIn = false;
  userImage: string | null = null;
  isAuthenticated = false;
  user: UserEntityRelationWithoutUserDataResponse;
  userObj: UserResponse;
  companies: CompanyResponse[]
  superAdmin: boolean = false;
  private authSubscription: Subscription | undefined;
  private routerSubscription: Subscription | undefined;
  private companyListSubscription: Subscription | undefined;
  isMobileMenuOpen = false;
  currentLang: string = 'no';

  languages = [
    { code: 'no', label: 'Norsk', flag: '🇳🇴' },
    { code: 'en', label: 'English', flag: '🇬🇧' }
  ];

  constructor(
    private autService: AuthService,
    private userService: UserService,
    private companyService: CompanyService,
    public storageService: StorageService,
    private authService: AuthService,
    private router: Router,
    private translateService: TranslateService) {
    this.currentLang = this.translateService.currentLang || 'no';
  }

  ngOnInit() {
    // Initialize language from localStorage (check both keys for backward compatibility)
    const savedLang = localStorage.getItem('language') || localStorage.getItem('preferred_language');
    if (savedLang && (savedLang === 'no' || savedLang === 'en')) {
      this.currentLang = savedLang;
      this.translateService.use(savedLang);
      // Ensure both keys are set
      localStorage.setItem('language', savedLang);
      localStorage.setItem('preferred_language', savedLang);
    }

    // Subscribe to authentication state changes
    this.authSubscription = this.authService.isAuthenticated.subscribe((isAuth) => {
      this.isAuthenticated = isAuth;

      if (isAuth) {
        // Load user data when authenticated
        this.loadUserData();
      } else {
        // Clear user data when not authenticated
        this.userObj = null!;
        this.user = null!;
        this.superAdmin = false;
      }
    });

    // Close mobile menu on route change
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.closeMobileMenu();
    });

    // Subscribe to company list changes (e.g., when a company is deleted)
    this.companyListSubscription = this.companyService.companyListChanged$.subscribe(() => {
      this.loadUserCompanies();
    });
  }

  ngAfterViewInit() {
    // Re-initialize KT Menu components after view loads
    this.initializeMenus();
  }

  ngOnDestroy() {
    // Unsubscribe to prevent memory leaks
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.companyListSubscription) {
      this.companyListSubscription.unsubscribe();
    }
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    // Lock/unlock body scroll when menu is open/closed
    if (this.isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    // Unlock body scroll
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    // Also close Bootstrap collapse if it's open
    const navElement = document.getElementById('mainNav');
    if (navElement && navElement.classList.contains('show')) {
      navElement.classList.remove('show');
    }
  }

  initializeMenus() {
    // Re-initialize all KT Menu instances
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.init();
        console.log('KT Menu initialized');
      }
    }, 100);
  }

  loadUserData() {
    this.userService.getUser().subscribe((data) => {
      this.userObj = data.data;
      console.log("User Obj in Header:", this.userObj)
      if (!this.userObj.profile_image_url) {
      }
      console.log(this.userObj.profile_image_url)
    });

    // Load user's companies
    this.loadUserCompanies();

    let token = this.authService.getToken()
    if (token) {
      console.log("token:", token)
      for (const key in token.role_access) {
        if (token.role_access[key].includes('BWN-0')) {
          this.superAdmin = true;
          // Super admins get all companies
          this.getAllCompanies()
          console.log('Super Admin');
          break;
        }
      }
    }
  }

  loadUserCompanies() {
    this.userService.getUserEntityRelation().subscribe({
      next: (entities) => {
        // Safely get selected company ID (silent mode returns null instead of throwing)
        const selectedCompanyId = this.storageService.getSelectedCompanyId(true);
        const storedCompany = this.storageService.getSelectedCompany();

        // Find the selected company relation, or default to first one
        if (entities && entities.length > 0) {
          if (selectedCompanyId) {
            this.user = entities.find((relation) => relation.entity_id == selectedCompanyId)!;
          }
          // If no selected company or selected company not found, select the first one
          // BUT: For super admins, if they have a stored company, don't overwrite it
          // (they can select companies they're not members of)
          if (!this.user && entities.length > 0) {
            if (this.superAdmin && storedCompany?.entity_id) {
              this.user = storedCompany;
            } else {
              this.user = entities[0];
              this.storageService.saveSelectedCompany(entities[0]);
            }
          }
        } else {
          this.user = null!;
        }

        // If not super admin, load company details for each entity
        // Use public endpoint to avoid JWT token race conditions after creating a company
        if (!this.superAdmin && entities && entities.length > 0) {
          const companyRequests = entities.map(entity =>
            this.companyService.getPublicCompanyById(entity.entity_id).pipe(
              catchError(() => of(null))
            )
          );

          forkJoin(companyRequests).subscribe({
            next: (companies) => {
              this.companies = companies.filter(c => c !== null) as CompanyResponse[];
              console.log("User companies loaded:", this.companies);
              // Re-initialize menus after companies are loaded
              this.initializeMenus();
            }
          });
        } else if (!this.superAdmin) {
          // No entities, clear companies list
          this.companies = [];
          this.initializeMenus();
        }
      }
    });
  }

  onLogin() {
    this.isLoggedIn = true;
    this.userImage = null; // replace with actual user profile image
  }

  getAllCompanies() {
    let payload: COR_COY_4 = {
      paginate: 0,
    }

    this.companyService.getAllCompanies(payload).subscribe((data) => {
      this.companies = data.data;
      console.log("Companies: ", this.companies)
      // Re-initialize menus after companies are loaded
      this.initializeMenus();
    })
  }


  onSignOut() {
    this.autService.logout()
  }

  changeLanguage(langCode: string) {
    this.currentLang = langCode;
    this.translateService.use(langCode);
    // Store in both keys for backward compatibility
    localStorage.setItem('preferred_language', langCode);
    localStorage.setItem('language', langCode);
  }

  getCurrentLanguage() {
    return this.languages.find(l => l.code === this.currentLang);
  }
}
